import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Maximize2, Minimize2, X } from 'lucide-react';
import Tooltip from '../../glass/Tooltip';
import type {
  AppModule,
  Layer,
  LayerGeometryType,
  LayerRenderer,
  RendererCategory,
} from '../../../types';
import { arcgisServiceUrlFor, getArcgisBaseUrl, withArcgisToken } from '../../../utils/arcgis';
import { getLayerDisplayName } from '../../../layers/capabilities';
import { resolveGeometryType } from '../../../layers/geometry';
import {
  buildSld,
  fetchWfsDistinctValues,
  fetchWfsFields,
  isSldTooLarge,
} from '../../../layers/sld';
import PointSymbologyPanel from './PointSymbologyPanel';
import LineSymbologyPanel from './LineSymbologyPanel';
import PolygonSymbologyPanel from './PolygonSymbologyPanel';
import { PropertySection } from './SymbolProperties';
import UniqueValueClassList from './UniqueValueClassList';

const CATEGORY_PALETTE = [
  '#2563EB', '#0D9488', '#D97706', '#DC2626', '#7C3AED',
  '#0891B2', '#65A30D', '#DB2777', '#475569', '#B45309',
];

export const DEFAULT_RENDERER: LayerRenderer = {
  type: 'single',
  color: '#3B82F6',
  fillOpacity: 0.25,
  outlineColor: '#1D4ED8',
  outlineWidth: 2,
  pointSize: 18,
  rotation: 0,
  symbolId: 'circle',
  lineStyle: 'solid',
  fillStyle: 'solid',
  outlineStyle: 'solid',
  defaultColor: '#94A3B8',
};

/** Points read better with a more opaque fill than polygons. */
export function defaultRendererFor(geometry: string): LayerRenderer {
  if (geometry === 'point') return { ...DEFAULT_RENDERER, fillOpacity: 0.9, pointSize: 18 };
  if (geometry === 'line') return { ...DEFAULT_RENDERER, fillOpacity: 1, outlineWidth: 3 };
  return DEFAULT_RENDERER;
}

interface FieldOption {
  name: string;
  alias: string;
}

/**
 * Right-side symbology properties panel.
 *
 * Slides in over the map rather than opening a centred modal, so the user can
 * see the live preview while editing. Edits go to a DRAFT renderer that is
 * previewed on the map immediately; Apply commits it, Cancel restores the
 * renderer that was active when the panel opened.
 */
export default function SymbologyPanel({
  layer,
  module,
  open,
  onPreview,
  onApply,
  onGeometryResolved,
  onClose,
}: {
  layer: Layer | null;
  module: AppModule;
  open: boolean;
  /** Live preview — not persisted. */
  onPreview: (renderer: LayerRenderer) => void;
  /** Commit. */
  onApply: (renderer: LayerRenderer) => void;
  /** Caches a geometry type discovered by the panel back onto the layer. */
  onGeometryResolved?: (layerId: string, geometryType: LayerGeometryType) => void;
  onClose: () => void;
}) {
  /**
   * Resolved geometry drives which editor mounts. Layers saved before geometry
   * capture existed (and MapServer/WMS layers the service did not describe)
   * arrive as 'unknown', so the panel resolves on open rather than guessing.
   */
  const [resolvedGeometry, setResolvedGeometry] = useState<LayerGeometryType>(
    layer?.geometryType ?? 'unknown',
  );
  const [resolving, setResolving] = useState(false);

  const geometry: LayerGeometryType = resolvedGeometry;
  /**
   * WMS layers are server-rendered via SLD, which constrains the symbol set
   * (and rules out client-side-only features like clustering).
   *
   * "No arcgisLayerId" is not the same as "is a WMS layer" — a locally
   * imported file has neither an arcgisLayerId NOR a GeoServer type to ask
   * for anything, so it must be excluded here too, or it gets treated as a
   * WMS layer that happens to point at a service it was never added from:
   * distinct-value lookups go out as WFS GetFeature requests for a type name
   * GeoServer has never heard of (a 400), and clustering — which a local
   * layer can absolutely use — gets hidden as if it were SLD-only.
   */
  const isWmsLayer = Boolean(layer) && layer!.arcgisLayerId === undefined && !layer!.localData;
  const committedRenderer = useMemo<LayerRenderer>(
    () => ({ ...defaultRendererFor(geometry), ...(layer?.renderer ?? {}) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layer?.id, geometry],
  );

  /**
   * Three distinct renderer states, per the state model:
   *  - sessionRenderer : what was applied when this editing session opened.
   *                      Cancel restores THIS. Re-captured on Apply so a later
   *                      Cancel cannot roll back changes the user committed.
   *  - draft           : the working renderer, previewed live on the map.
   *  - layer.renderer  : the persisted/applied renderer (owned by the Builder).
   */
  const [sessionRenderer, setSessionRenderer] = useState<LayerRenderer>(committedRenderer);
  const [draft, setDraft] = useState<LayerRenderer>(committedRenderer);
  const [fields, setFields] = useState<FieldOption[]>([]);
  const [loadingValues, setLoadingValues] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  /** Docked (small, right-2/top-2) by default — see the panel's own JSX for
   *  where this widens it. Reset per layer session, same as everything else
   *  below, so maximising one layer's panel doesn't carry into the next. */
  const [maximised, setMaximised] = useState(false);

  // Start a fresh session whenever a different layer's panel is opened.
  useEffect(() => {
    setDraft(committedRenderer);
    setSessionRenderer(committedRenderer);
    setDirty(false);
    setError(null);
    setMaximised(false);
  }, [committedRenderer, layer?.id]);

  /* Resolve geometry from service metadata whenever the panel opens. */
  useEffect(() => {
    if (!open || !layer) return;

    const cached = layer.geometryType;
    if (cached && cached !== 'unknown') {
      setResolvedGeometry(cached);
      return;
    }

    let cancelled = false;
    setResolving(true);
    void resolveGeometryType(layer, module)
      .then((geometryType) => {
        if (cancelled) return;
        setResolvedGeometry(geometryType);
        // Cache it on the layer so the next open is instant.
        if (geometryType !== 'unknown') onGeometryResolved?.(layer.id, geometryType);
      })
      .finally(() => {
        if (!cancelled) setResolving(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, layer?.id, layer?.geometryType]);

  /**
   * WMS styling travels in the tile URL as SLD_BODY, which servers cap in
   * length. Warn in the UI when the current classification would exceed it —
   * otherwise the map silently falls back to default styling with only a
   * console message.
   */
  const sldTooLarge = useMemo(() => {
    if (!isWmsLayer || !layer) return false;
    return isSldTooLarge(buildSld(layer.name, draft, geometry));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWmsLayer, layer?.name, draft, geometry]);

  const update = (changes: Partial<LayerRenderer>) => {
    const next = { ...draft, ...changes };
    setDraft(next);
    setDirty(true);
    onPreview(next); // live map preview
  };

  /**
   * Replaces ONE unique-value class's colour and/or symbol fields.
   *
   * Targets by index rather than value: category values are not guaranteed
   * unique once stringified, so matching by value could restyle the wrong
   * class. The renderer's global fields are deliberately untouched — only
   * this one category's own overrides change (see RendererCategory /
   * mergeCategoryStyle).
   */
  const updateCategory = (index: number, changes: Partial<RendererCategory>) => {
    const categories = draft.categories ?? [];
    if (index < 0 || index >= categories.length) return;
    const next = categories.map((category, position) =>
      position === index ? { ...category, ...changes } : category,
    );
    update({ categories: next });
  };

  /* Field list for the unique-value renderer — ArcGIS metadata or WFS. */
  useEffect(() => {
    if (!open || !layer) return;
    let cancelled = false;

    (async () => {
      try {
        // A file-backed layer carries its own features, so the field list comes
        // straight from them — there is no service metadata to ask for.
        if (layer.localData) {
          const names = Array.from(
            new Set(
              (layer.localData.features ?? []).flatMap((feature) =>
                Object.keys(feature.properties ?? {}),
              ),
            ),
          );
          if (!cancelled) setFields(names.map((name) => ({ name, alias: name })));
          return;
        }

        if (isWmsLayer) {
          const wfsFields = await fetchWfsFields(module.geoServerUrl, layer.name);
          if (!cancelled) setFields(wfsFields);
          return;
        }

        const symbologyServiceUrl = arcgisServiceUrlFor(layer, module);
        const baseUrl = getArcgisBaseUrl(symbologyServiceUrl);
        if (!baseUrl || layer.arcgisLayerId === undefined) return;
        const response = await fetch(withArcgisToken(`${baseUrl}/${layer.arcgisLayerId}?f=json`, symbologyServiceUrl));
        if (!response.ok) return;
        const data = await response.json();
        if (cancelled || data?.error) return;
        setFields(
          (data.fields ?? [])
            .filter((field: any) =>
              ['esriFieldTypeString', 'esriFieldTypeSmallInteger', 'esriFieldTypeInteger'].includes(
                field.type,
              ),
            )
            .map((field: any) => ({ name: field.name, alias: field.alias || field.name })),
        );
      } catch (err) {
        console.error('[symbology] field list failed', err);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, module.arcgisUrl, module.geoServerUrl, layer?.id, isWmsLayer]);

  const buildCategories = useCallback(
    async (fieldName: string) => {
      if (!layer || !fieldName) return;

      setLoadingValues(true);
      setError(null);
      try {
        let values: string[];

        if (layer.localData) {
          // Already in the browser — no service to ask, so read the field
          // straight off the features themselves, the same way the field
          // LIST above does for a local layer.
          values = Array.from(
            new Set(
              (layer.localData.features ?? [])
                .map((feature) => feature.properties?.[fieldName])
                .filter((value) => value !== null && value !== undefined && value !== '')
                .map((value) => String(value)),
            ),
          ).slice(0, 20);
        } else if (isWmsLayer) {
          // GeoServer has no distinct-values operation; WFS reads and de-dupes.
          values = await fetchWfsDistinctValues(module.geoServerUrl, layer.name, fieldName);
        } else {
          const distinctValuesServiceUrl = arcgisServiceUrlFor(layer, module);
          const baseUrl = getArcgisBaseUrl(distinctValuesServiceUrl);
          if (!baseUrl || layer.arcgisLayerId === undefined) return;
          const params = new URLSearchParams({
            where: '1=1',
            outFields: fieldName,
            returnDistinctValues: 'true',
            returnGeometry: 'false',
            f: 'json',
          });
          const response = await fetch(
            withArcgisToken(`${baseUrl}/${layer.arcgisLayerId}/query?${params}`, distinctValuesServiceUrl),
          );
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const data = await response.json();
          if (data?.error) throw new Error(data.error.message);

          values = Array.from(
            new Set(
              (data.features ?? [])
                .map((feature: any) => feature?.attributes?.[fieldName])
                .filter((value: unknown) => value !== null && value !== undefined && value !== '')
                .map((value: unknown) => String(value)),
            ),
          ).slice(0, 20) as string[];
        }

        if (values.length === 0) {
          setError('That field has no distinct values to categorize.');
          return;
        }
        // Normalise once, then de-duplicate on the normalised key so values that
        // differ only by stray whitespace do not become separate classes.
        const seen = new Set<string>();
        const categories: RendererCategory[] = [];
        for (const raw of values) {
          const value = String(raw).trim();
          if (value === '' || seen.has(value)) continue;
          seen.add(value);
          categories.push({
            value, // matching key — the actual field value
            label: value, // display text, kept separate from the key
            color: CATEGORY_PALETTE[categories.length % CATEGORY_PALETTE.length],
          });
        }

        if (categories.length === 0) {
          setError('That field has no distinct values to categorize.');
          return;
        }

        // Switching field must fully replace the previous classification —
        // stale classes from the old field are dropped, not merged.
        update({ type: 'unique', field: fieldName, categories });
      } catch (err) {
        console.error('[symbology] distinct values failed', err);
        setError('Unable to load unique values for this field.');
      } finally {
        setLoadingValues(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [module.arcgisUrl, module.geoServerUrl, layer?.id, isWmsLayer, draft],
  );

  // Escape cancels (restores the committed renderer).
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') cancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, committedRenderer]);

  /** Discards unapplied edits and restores the renderer this session started with. */
  const cancel = () => {
    onPreview(sessionRenderer);
    setDraft(sessionRenderer);
    setDirty(false);
    onClose();
  };

  const apply = () => {
    onApply(draft);
    // The applied renderer becomes the new session baseline, so a subsequent
    // Cancel discards only edits made AFTER this Apply.
    setSessionRenderer(draft);
    setDirty(false);
  };

  /**
   * Restores default colours and symbols.
   *
   * For a unique-value renderer this re-applies the default palette to the
   * EXISTING classes and clears every per-category symbol override (so each
   * class goes back to sharing the renderer's one global symbol) — it must
   * not drop the renderer type, field, or class list, which would silently
   * discard the user's classification.
   */
  const reset = () => {
    const base = defaultRendererFor(geometry);
    const fresh: LayerRenderer =
      draft.type === 'unique' && draft.categories?.length
        ? {
            ...base,
            type: 'unique',
            field: draft.field,
            categories: draft.categories.map((category, index) => ({
              value: category.value,
              label: category.label,
              color: CATEGORY_PALETTE[index % CATEGORY_PALETTE.length],
            })),
            defaultColor: DEFAULT_RENDERER.defaultColor,
          }
        : base;

    setDraft(fresh);
    setDirty(true);
    onPreview(fresh);
  };

  return (
    <aside
      role="dialog"
      aria-label="Symbology"
      aria-hidden={!open}
      // Floating glass properties panel over the map. `--over-map` adds the
      // extra edge/shadow that keeps it legible above satellite imagery.
      // Docked small (right-2/top-2, 320px) by default — the exact same spot
      // it has always opened in. Maximise only WIDENS it (left-2 as well),
      // same as the attribute table's own maximise, so there is more room to
      // see every class at once without hunting through a tall scroll list;
      // it never changes where the panel lives unless the user asks it to.
      className={`gl-card gl-surface--strong gl-surface--over-map absolute top-2 z-[4000] flex flex-col overflow-hidden transition-[transform,left,width] duration-300 ease-out ${
        open ? 'translate-x-0' : 'pointer-events-none invisible translate-x-[calc(100%+1rem)]'
      } ${maximised ? 'left-2 right-2' : 'right-2 w-[320px]'}`}
      style={{
        height: 'calc(100% - 1rem)',
        borderRadius: 'var(--glass-radius-lg)',
        background: 'var(--glass-bg-strong)',
        // Only filter while shown — a backdrop-filter on a hidden, always-mounted
        // panel still blurs whatever is behind it.
        backdropFilter: open ? 'blur(var(--glass-blur)) saturate(var(--glass-saturate))' : undefined,
        WebkitBackdropFilter: open
          ? 'blur(var(--glass-blur)) saturate(var(--glass-saturate))'
          : undefined,
        border: '1px solid var(--glass-border)',
        boxShadow: 'var(--glass-shadow-lg)',
      }}
    >
      {/* Header */}
      <header className="gl-header flex flex-shrink-0 items-start justify-between px-4 py-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-[#0F2942]">Symbology</h3>
          <p className="truncate text-xs text-slate-500" title={layer ? getLayerDisplayName(layer) : ''}>
            {layer ? getLayerDisplayName(layer) : '—'}
          </p>
          {layer && (
            <span className="mt-1 inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
              {geometry === 'unknown' ? 'vector' : geometry}
            </span>
          )}
        </div>
        <div className="flex flex-shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => setMaximised((value) => !value)}
            aria-label={maximised ? 'Restore symbology panel' : 'Maximise symbology panel'}
            title={maximised ? 'Restore' : 'Maximise'}
            className="rounded p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          >
            {maximised ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={cancel}
            aria-label="Close symbology"
            className="rounded p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </header>

      {/* Body */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {!layer ? null : (
          <>
            {/* Renderer type */}
            <PropertySection title="Renderer">
              <div className="grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1">
                <Tooltip content="Draw every feature in this layer with the same symbol.">
                <button
                  type="button"
                  onClick={() => update({ type: 'single' })}
                  className={`rounded-md px-2 py-1 text-[11px] font-medium transition ${
                    draft.type === 'single' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500'
                  }`}
                >
                  Single symbol
                </button>
                </Tooltip>
                <Tooltip content="Colour features by the value of a field, one colour per distinct value.">
                <button
                  type="button"
                  onClick={() => update({ type: 'unique' })}
                  className={`rounded-md px-2 py-1 text-[11px] font-medium transition ${
                    draft.type === 'unique' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500'
                  }`}
                >
                  Unique values
                </button>
                </Tooltip>
              </div>

              {draft.type === 'unique' && (
                <div className="mt-2 space-y-2">
                  <select
                    value={draft.field ?? ''}
                    onChange={(event) => void buildCategories(event.target.value)}
                    disabled={loadingValues}
                    className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-blue-400 disabled:opacity-60"
                  >
                    <option value="">-- Select a field --</option>
                    {fields.map((field) => (
                      <option key={field.name} value={field.name}>
                        {field.alias}
                      </option>
                    ))}
                  </select>
                  {loadingValues && <p className="text-[11px] text-slate-400">Reading values…</p>}
                  {draft.categories && draft.categories.length > 0 && (
                    <UniqueValueClassList
                      categories={draft.categories}
                      defaultColor={draft.defaultColor ?? '#94A3B8'}
                      geometry={geometry}
                      renderer={draft}
                      sldOnly={isWmsLayer}
                      expanded={maximised}
                      onCategoryChange={updateCategory}
                      onDefaultColorChange={(color) => update({ defaultColor: color })}
                    />
                  )}
                </div>
              )}
            </PropertySection>

            {/* Geometry-specific editor — only the relevant controls are shown */}
            {/* STRICT geometry routing.
                The point gallery is mounted ONLY for a confirmed point layer.
                An unresolved geometry falls back to the polygon (colour-based)
                editor, never to point symbols — showing icons for a line or
                polygon layer is the failure mode this guards against. */}
            {(() => {
              const unknownNotice = geometry === 'unknown' && (
                <p className="mx-4 mt-2 rounded bg-amber-50 px-2 py-1.5 text-[10px] leading-relaxed text-amber-800">
                  The service did not report a geometry type for this layer. Colour-based styling
                  is shown.
                </p>
              );
              const editor =
                geometry === 'point' ? (
                  <PointSymbologyPanel renderer={draft} onChange={update} sldOnly={isWmsLayer} />
                ) : geometry === 'line' ? (
                  <LineSymbologyPanel renderer={draft} onChange={update} />
                ) : (
                  <PolygonSymbologyPanel renderer={draft} onChange={update} />
                );

              if (resolving) {
                return (
                  <p className="px-4 py-8 text-center text-xs text-slate-400">
                    Detecting layer geometry…
                  </p>
                );
              }

              // Unique-value mode: everything in here edits the renderer's
              // DEFAULT/fallback symbol — the one "All other values" draws
              // with, and the one a class keeps using until it is given its
              // own symbol via that class's own box in the list above. It is
              // NOT a per-class editor, so changing it visibly moves every
              // class with no override of its own (every class, right after
              // classifying a field) — collapsed by default so it cannot be
              // reached for instead of a class's own box, with a label and
              // (when opened) a banner that says exactly what it controls.
              if (draft.type === 'unique') {
                return (
                  <details className="mx-4 mt-3 mb-1 overflow-hidden rounded-lg border border-blue-100">
                    <summary className="cursor-pointer select-none bg-blue-50 px-2.5 py-2 text-[11px] font-medium text-blue-800">
                      Default symbol <span className="font-normal text-blue-700">(advanced)</span>
                    </summary>
                    <div className="pt-1">
                      <p className="mx-2.5 mt-2 rounded bg-blue-50 px-2 py-1.5 text-[10px] leading-relaxed text-blue-800">
                        Used by classes above with no symbol of their own, and by &ldquo;All other
                        values&rdquo;. To restyle just ONE class, use that class&rsquo;s own box in
                        the list above instead.
                      </p>
                      {unknownNotice}
                      {editor}
                    </div>
                  </details>
                );
              }

              return (
                <>
                  {unknownNotice}
                  {editor}
                </>
              );
            })()}

            {sldTooLarge && (
              <p
                role="alert"
                className="mx-4 mb-3 rounded bg-amber-50 px-2 py-1.5 text-[11px] leading-relaxed text-amber-800"
              >
                Too many classes for this layer&rsquo;s styling to be sent to GeoServer, so the
                map is showing default styling. Choose a field with fewer unique values.
              </p>
            )}

            {error && (
              <p role="alert" className="mx-4 mb-3 rounded bg-red-50 px-2 py-1.5 text-[11px] text-red-600">
                {error}
              </p>
            )}
          </>
        )}
      </div>

      {/* Footer actions */}
      <footer className="flex flex-shrink-0 items-center justify-between gap-2 border-t border-slate-200 px-4 py-3">
        <button
          type="button"
          onClick={reset}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
        >
          Reset
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={cancel}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={!dirty}
            className="rounded-lg bg-[#0F2942] px-4 py-1.5 text-xs font-semibold text-white transition hover:bg-[#16395C] disabled:opacity-40"
          >
            Apply
          </button>
        </div>
      </footer>
    </aside>
  );
}
